import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

/* ===== TradeMode — THE one switch between 📄 PAPER and 🔴 LIVE =====
   Backed by a single backend flag (scanner config `simulate`) which the code
   audit shows is read at exactly one decision point: order placement in the
   guarded scanner. Signals, guards, sizing, tiers, exits are identical in
   both modes — each trade carries its own simulated flag, so flipping the
   switch never affects trades already open.
   Going LIVE requires typing the word LIVE (the app-level second confirm).
   The pipe test places one far-OTM ₹0.05 LIMIT (max cost ~₹3.25), verifies
   it reaches Zerodha, and cancels it — proving the live path end-to-end. */

export default function TradeMode() {
  const [st, setSt] = useState(null);
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [pipe, setPipe] = useState(null);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await axios.get("/api/zerodha/trade-mode", { timeout: 10000 });
      if (r.data.success) setSt(r.data);
    } catch {}
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, [load]);

  const setMode = async (mode) => {
    setBusy(true); setMsg("");
    try {
      const body = mode === "live" ? { mode, confirm: confirmText } : { mode };
      const r = await axios.post("/api/zerodha/trade-mode", body);
      if (r.data.success) { setMsg(`Mode: ${r.data.mode.toUpperCase()}`); setConfirmText(""); load(); }
    } catch (e) { setMsg(e.response?.data?.message || e.message); }
    setBusy(false);
  };

  const runPipeTest = async () => {
    setBusy(true); setPipe({ running: true });
    try {
      const r = await axios.post("/api/zerodha/live-pipe-test", { confirm: true }, { timeout: 30000 });
      setPipe(r.data);
    } catch (e) { setPipe({ pass: false, steps: [{ step: "request", ok: false, detail: e.response?.data?.message || e.message }] }); }
    setBusy(false);
  };

  if (!st) return null;
  const live = st.mode === "live";
  const r = st.readiness || {};

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button onClick={() => setOpen(o => !o)} title="Trade mode — click to manage"
        style={{
          fontWeight: 800, fontSize: 12, padding: "4px 14px", borderRadius: 14, cursor: "pointer",
          background: live ? "#dc2626" : "var(--btn-muted)",
          color: live ? "#fff" : "var(--grey-light)",
          border: live ? "1px solid #b91c1c" : "1px solid var(--btn-border)",
          animation: live ? "pulse 2s infinite" : undefined,
        }}>
        {live ? "🔴 LIVE" : "📄 PAPER"}
      </button>

      {open && (
        <div style={{ position: "absolute", top: "110%", right: 0, zIndex: 500, width: 360, background: "var(--panel2, #1b1f27)", border: "1px solid var(--panel3)", borderRadius: 10, padding: 14, boxShadow: "0 8px 30px rgba(0,0,0,.4)" }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Trade mode: {live ? "🔴 LIVE — real orders on Zerodha" : "📄 PAPER — no real orders"}</div>

          <div style={{ fontSize: 11.5, color: "var(--muted2)", lineHeight: 1.6, marginBottom: 10 }}>
            Kite auth: <b className={r.authenticated ? "positive" : "negative"}>{String(r.authenticated)}</b>
            {" · "}funds: <b>{r.funds != null ? `₹${Math.round(r.funds).toLocaleString("en-IN")}` : "—"}</b>
            {" · "}market: <b>{r.marketOpen ? "open" : "closed"}</b><br />
            books: <b>NIFTY, BANKNIFTY, SENSEX</b> (plain — the paper-record ruleset) @ {st.lots} lots
          </div>

          {live ? (
            <button className="btn btn-sm btn-primary" disabled={busy} onClick={() => setMode("paper")}
              style={{ width: "100%", marginBottom: 8 }}>Switch to 📄 PAPER (immediate)</button>
          ) : (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: "var(--muted2)", marginBottom: 4 }}>
                To go live, type <b>LIVE</b> — the SAME books, SAME signals and rules place real orders instead of paper ones. Nothing else changes. (Guarded lab stands down while live.)
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <input className="ema-select" value={confirmText} onChange={e => setConfirmText(e.target.value)}
                  placeholder="type LIVE" style={{ width: 110 }} />
                <button className="btn btn-sm btn-danger" disabled={busy || confirmText !== "LIVE" || !r.authenticated}
                  onClick={() => setMode("live")}>Go 🔴 LIVE</button>
              </div>
            </div>
          )}

          <div style={{ borderTop: "1px solid var(--panel3)", paddingTop: 8, marginTop: 4 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button className="btn btn-sm btn-secondary" disabled={busy || !r.authenticated || !r.marketOpen} onClick={runPipeTest}>
                🧪 Test live pipe
              </button>
              <span style={{ fontSize: 10, color: "var(--muted2)" }}>
                places ONE far-OTM ₹0.05 LIMIT (max cost ~₹3.25), confirms at Zerodha, cancels it
              </span>
            </div>
            {pipe && (
              <div style={{ marginTop: 8, fontSize: 11 }}>
                {pipe.running ? "running…" : (
                  <>
                    <b className={pipe.pass ? "positive" : "negative"}>{pipe.pass ? "✅ PIPE VERIFIED" : "❌ ISSUES"}</b>
                    {(pipe.steps || []).map((s, i) => (
                      <div key={i} style={{ color: s.ok ? "var(--muted2)" : "var(--pale-neg, #f87171)" }}>
                        {s.ok ? "✓" : "✗"} {s.step}: {s.detail}
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
          {msg && <div style={{ marginTop: 8, fontSize: 11 }} className="positive">{msg}</div>}
        </div>
      )}
    </div>
  );
}
